import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Remplace la description quand le visiteur change de déclinaison.
 *
 * Shopify n'a qu'une description par produit. Celle propre à chaque référence
 * Dreamlove vit dans le métachamp de variante `custom.description`, résolu en
 * Liquid par `snippets/description-produit.liquid`. Restait à la rafraîchir :
 * sur un changement de variante ordinaire, le sélecteur ne remplace que
 * lui-même, et seuls les composants qui écoutent `variant:update` se mettent à
 * jour, soit le prix, le stock, le SKU et la galerie. La description ne bougeait
 * pas.
 *
 * Aucune requête n'est faite ici. Le sélecteur a déjà rechargé la page pour se
 * mettre à jour et passe le document obtenu dans `event.detail.data.html` : la
 * description de la nouvelle variante y est rendue. Une requête de plus par clic
 * n'apporterait rien.
 *
 * L'écoute est posée sur `document`, pas sur la section : les autres composants
 * du thème vivent dans la section du sélecteur et reçoivent l'événement en
 * remontant, mais l'accordéon de détails est une section soeur. Un événement ne
 * traverse pas latéralement.
 */
class DescriptionVariante extends Component {
  connectedCallback() {
    super.connectedCallback();
    // Sans rôle, l'élément n'est qu'un conteneur : rien à réécrire.
    if (!this.dataset.role) return;
    document.addEventListener(ThemeEvents.variantUpdate, this.#surChangement);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.variantUpdate, this.#surChangement);
  }

  /**
   * @param {Event} event - L'événement de changement de variante.
   */
  #surChangement = (event) => {
    const detail = /** @type {any} */ (event).detail;

    // Les cartes produit et l'ajout rapide diffusent le même événement, et il
    // remonte jusqu'à `document` comme les autres. Sans ce garde-fou, changer la
    // déclinaison d'une recommandation en bas de page réécrirait la fiche.
    const produit = detail?.data?.newProduct?.id ?? detail?.data?.productId;
    if (!produit || String(produit) !== this.dataset.productId) return;

    const nouveau = detail?.data?.html?.querySelector(
      `description-variante[data-role="${this.dataset.role}"]`
    );
    // Absent : la réponse était un fragment de carte, pas la page produit. On
    // garde l'affichage en place plutôt que de le vider.
    if (!nouveau) return;

    this.innerHTML = nouveau.innerHTML;
  };
}

if (!customElements.get('description-variante')) {
  customElements.define('description-variante', DescriptionVariante);
}
